import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { BudgetLineRow, TimeEntry } from "@/hooks/use-project-detail";
import { computeBudgetLineLoggedHours } from "@/hooks/use-budget-line-logged-hours";
import type { LineGuardContext } from "@/lib/budget-revision";

/**
 * VIB-1202: bygger vern-konteksten per budsjettlinje for revisjonsmodus —
 * loggede timer, Σ fullført/godkjent tildelte timer, og om en fullført
 * tildeling ligger i en LÅST måned (monthly_confirmations).
 */

const COMPLETED_STATUSES = new Set(["completed", "approved"]);

/** yyyy-mm-dd / ISO → { year, month } (måned 1-12). Ingen tz-finesse — vernet
 *  er konservativt og en dags skjevhet i månedsgrensen er akseptabelt her. */
function ymOf(dateStr: string): { year: number; month: number } | null {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/**
 * Ren beregning: per linje → LineGuardContext. `lockedMonths` er et sett av
 * `${user_id}-${year}-${month}` for bekreftede måneder.
 */
export function computeLineGuards(
  budgetLines: BudgetLineRow[],
  timeEntries: TimeEntry[],
  lockedMonths: Set<string>,
): Map<string, LineGuardContext> {
  const logged = computeBudgetLineLoggedHours(timeEntries, budgetLines);
  const result = new Map<string, LineGuardContext>();

  for (const bl of budgetLines) {
    let completedApprovedHours = 0;
    let hasLockedMonthCompletion = false;

    for (const a of bl.assignments ?? []) {
      if (!COMPLETED_STATUSES.has(a.task_status)) continue;
      completedApprovedHours += Number(a.hours ?? 0);
      if (a.completed_at && a.user_id) {
        const ym = ymOf(a.completed_at);
        if (ym && lockedMonths.has(`${a.user_id}-${ym.year}-${ym.month}`)) {
          hasLockedMonthCompletion = true;
        }
      }
    }

    result.set(bl.id, {
      loggedHours: logged.totals.get(bl.id) ?? 0,
      completedApprovedHours,
      hasLockedMonthCompletion,
    });
  }

  return result;
}

/** Distinkte user_id fra fullførte/godkjente tildelinger på prosjektets linjer. */
function assigneeIdsWithCompletion(budgetLines: BudgetLineRow[]): string[] {
  const ids = new Set<string>();
  for (const bl of budgetLines) {
    for (const a of bl.assignments ?? []) {
      if (COMPLETED_STATUSES.has(a.task_status) && a.user_id) ids.add(a.user_id);
    }
  }
  return [...ids];
}

export function useBudgetRevisionGuards(
  projectId: string | undefined,
  budgetLines: BudgetLineRow[],
  timeEntries: TimeEntry[],
) {
  const userIds = assigneeIdsWithCompletion(budgetLines);

  return useQuery({
    queryKey: ["budget-revision-guards", projectId, userIds.sort().join(",")],
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<Map<string, LineGuardContext>> => {
      const lockedMonths = new Set<string>();
      if (userIds.length > 0) {
        const { data, error } = await supabase
          .from("monthly_confirmations")
          .select("user_id, year, month")
          .in("user_id", userIds);
        if (error) throw error;
        for (const r of data ?? []) {
          lockedMonths.add(`${r.user_id}-${r.year}-${r.month}`);
        }
      }
      return computeLineGuards(budgetLines, timeEntries, lockedMonths);
    },
  });
}
